import { getDateInLocalTZFromDateStr } from "../datetime";

/**
 * Helper function to group scopes by day.
 * @param {Array} scopes - Array of scope objects.
 * @returns {Map} - Map of scopes grouped by day (0=Sunday, 1=Monday, etc.).
 */
export function groupScopesByDay(scopes) {
  return Map.groupBy(scopes, (scope) => {
    const scopeDate = getDateInLocalTZFromDateStr(scope.date);
    return scopeDate.getDay();
  });
}

/**
 * Helper function to map dates of the week to their corresponding scopes.
 * @param {Array} datesOfWeek - Array of date objects representing the week.
 * @param {Map} scopesByDay - Map of scopes grouped by day.
 * @returns {Array} - Array of tuples containing date and corresponding scopes.
 */
export function mapScopesByDate(datesOfWeek, scopesByDay) {
  return datesOfWeek.map((date) => {
    const weekday = date.getDay();
    return [date, scopesByDay.get(weekday) ?? []];
  });
}

/**
 * Display label for a WorkScope or WorkScopeAspect-shaped object.
 *
 * Supports Dispatch rows (`scope.scope.title`) and Schedule blocks/catalog rows
 * (`scope.name` / `scope.title`) so notification and UI code can avoid repeating
 * nested title/name fallback chains.
 *
 * @param {Object|null|undefined} scopeLike
 * @param {string|number} [fallback="scope"]
 * @returns {string|number}
 */
export function getScopeDisplayName(scopeLike, fallback = "scope") {
  return (
    scopeLike?.scope?.title ??
    scopeLike?.scope?.name ??
    scopeLike?.title ??
    scopeLike?.name ??
    scopeLike?.id ??
    fallback
  );
}

/**
 * Primary scope notes plus extra_data_sections for SMS and daily job reports.
 * Each extra section is "{title}: {body}"; parts are joined with newlines.
 *
 * @param {string|null|undefined} notes
 * @param {Array<{ title?: string, body?: string }>|null|undefined} extraDataSections
 * @returns {string}
 */
export function formatScopeNotesWithExtraSections(notes, extraDataSections) {
  const main = typeof notes === "string" ? notes : "";
  const sections = Array.isArray(extraDataSections) ? extraDataSections : [];
  const extraParts = sections.map((sec) => {
    const title = String(sec?.title ?? "").trim() || "Section";
    const body = String(sec?.body ?? "");
    return `${title}: ${body}`;
  });
  const parts = [];
  if (main.trim()) parts.push(main);
  parts.push(...extraParts);
  return parts.join("\n");
}

/**
 * Whether a scope aspect has note content: non-empty primary `notes`, or any
 * `extra_data_sections` entry with a non-empty body (primary may be blank).
 *
 * @param {{ notes?: string|null, extra_data_sections?: Array<{ body?: string }>|null }|null|undefined} scope
 * @returns {boolean}
 */
export function scopeAspectHasNotes(scope) {
  if (scope?.notes?.toString().trim()) {
    return true;
  }
  return (scope?.extra_data_sections ?? []).some(
    (section) => String(section?.body ?? "").trim().length > 0
  );
}